import React, { useCallback } from "react";

import Col from "antd/es/col";
import Form from "antd/es/form";
import { FormInstance } from "antd/es/form/hooks/useForm";
import Row from "antd/es/row";
import { useIntl } from "react-intl";

import Checkbox from "@app/component/input/Checkbox";
import layout from "@app/styles/layout";
import NotificationSettingValue from "@app/types/notifications/NotificationSetting";

import Card from "../Card";
import Header from "../Header";

import RecordUpdateFields from "./RecordUpdateFields";
import styles from "./RecordUpdatesCommon.module.scss";
import getRecordOwnershipLevelSettingsState from "./utils/getRecordOwnershipLevelSettingsState";
import getRecordSettingsState from "./utils/getRecordSettingsState";

interface Props {
  form: FormInstance<NotificationSettingValue>;
  onToggle: () => void;
}

const RecordUpdates: React.FC<Props> = ({ form, onToggle }) => {
  const intl = useIntl();
  const handleToggle = useCallback(
    (enabled: boolean) => {
      form.setFieldsValue({ recordUpdates: getRecordSettingsState(enabled) });
      onToggle();
    },
    [form, onToggle]
  );

  const handleChangeItem = useCallback(
    (name: "owned" | "shared") => (enabled: boolean) => {
      form.setFieldsValue({
        recordUpdates: { [name]: getRecordOwnershipLevelSettingsState(enabled) },
      });
    },
    [form]
  );
  return (
    <Card>
      <Form.Item hidden name="recordUpdates">
        <div />
      </Form.Item>
      <Form.Item
        shouldUpdate={(prev, next) => prev.recordUpdates.enabled !== next.recordUpdates.enabled}
      >
        {({ getFieldValue }) => (
          <Header
            checked={getFieldValue(["recordUpdates", "enabled"])}
            description={intl.formatMessage({
              id: "settings.notifications.recordUpdates.text",
              defaultMessage:
                "Receive an email notification each morning informing you of any records in need of attention.",
              description: "Text of settings notifications Record Updates card",
            })}
            onChange={handleToggle}
            title={intl.formatMessage({
              id: "settings.notifications.recordUpdates.title",
              defaultMessage: "Morning Update",
              description: "Title of settings notifications Record Updates card",
            })}
          />
        )}
      </Form.Item>
      <Row gutter={layout.spacerS} justify="space-between">
        <Col span={12}>
          <Form.Item
            className={styles.formItem}
            shouldUpdate={(prev, next) =>
              prev.recordUpdates.enabled !== next.recordUpdates.enabled ||
              prev.recordUpdates.owned.enabled !== next.recordUpdates.owned.enabled
            }
          >
            {({ getFieldValue }) => (
              <Checkbox
                checked={getFieldValue(["recordUpdates", "owned", "enabled"])}
                className={styles.primaryCheckboxText}
                disabled={!getFieldValue(["recordUpdates", "enabled"])}
                onChange={handleChangeItem("owned")}
              >
                <div className={styles.bold}>
                  {intl.formatMessage({
                    id: "settings.notifications.recordUpdates.owned",
                    defaultMessage: "Notify me for updates to records I own:",
                    description: "Settings notifications Record Updates - records you own",
                  })}
                </div>
              </Checkbox>
            )}
          </Form.Item>
          <RecordUpdateFields form={form} level="owned" />
        </Col>
        <Col span={12}>
          <Form.Item
            className={styles.formItem}
            shouldUpdate={(prev, next) =>
              prev.recordUpdates.enabled !== next.recordUpdates.enabled ||
              prev.recordUpdates.shared.enabled !== next.recordUpdates.shared.enabled
            }
          >
            {({ getFieldValue }) => (
              <Checkbox
                checked={getFieldValue(["recordUpdates", "shared", "enabled"])}
                className={styles.primaryCheckboxText}
                disabled={!getFieldValue(["recordUpdates", "enabled"])}
                onChange={handleChangeItem("shared")}
              >
                <div className={styles.bold}>
                  {intl.formatMessage({
                    id: "settings.notifications.recordUpdates.shared",
                    defaultMessage: "Notify me for updates to records I can view:",
                    description: "Settings notifications Record Updates -  you can view",
                  })}
                </div>
              </Checkbox>
            )}
          </Form.Item>
          <RecordUpdateFields form={form} level="shared" />
        </Col>
      </Row>
    </Card>
  );
};

export default RecordUpdates;
